/**
 * ScriptExecutionManager
 *
 * Forwards signed scripts unchanged, correlates IPC completions, and reports status.
 */

import {
  generators,
  ProvisioningMessageType,
  generateProvisioningMessage,
  type WebsocketMessage,
  type PlaycastMessageSystemLaunchElevatedPowerShellScriptReply,
  type PlaycastMessageSystemCancelProvisioningScriptReply,
} from '@playcastdotio/Messaging';
import type { IIpcService } from '../../../services/ipc/IIpcService';
import type { IRealtimeService } from '../../../services/realtime/IRealtimeService';
import type { QueuedScript } from './ScriptQueueManager';
import { createId } from '@playcastdotio/CreateId';

export type ScriptExecutionResult = {
  status: 'succeeded' | 'failed' | 'timeout' | 'cancelled';
  exitCode?: number;
  stdout?: string;
  stderr?: string;
  error?: string;
};

type ReplyCallback = (reply: PlaycastMessageSystemLaunchElevatedPowerShellScriptReply['message']) => void;

interface ScriptExecutionManagerDeps {
  ipcService: IIpcService;
  realtimeService: IRealtimeService;
  getMachineId: () => string;
  getHostId: () => string;
  ownership?: Pick<import('./ScriptQueueManager').ScriptQueueManager, 'isMaintenanceOwned'>;
}

export class ScriptExecutionManager {
  private readonly ipcService: IIpcService;
  private readonly realtimeService: IRealtimeService;
  private readonly getMachineId: () => string;
  private readonly getHostId: () => string;
  private readonly ownership: ScriptExecutionManagerDeps['ownership'];

  private pendingReplyCallback: ReplyCallback | null = null;
  private pendingRequestId: string | null = null;
  private ackReceived = false;
  private scriptSentAt: number | null = null;
  private cancellationRequested = false;

  constructor(deps: ScriptExecutionManagerDeps) {
    this.ipcService = deps.ipcService;
    this.realtimeService = deps.realtimeService;
    this.getMachineId = deps.getMachineId;
    this.getHostId = deps.getHostId;
    this.ownership = deps.ownership;
  }

  /**
   * Called when PlayJector acknowledges the provisioning script payload (validated; execution starting).
   * Does not complete {@link forwardToPlayjector}; the completion reply does.
   */
  handlePlayjectorAck(reply: { requestId?: string }): void {
    if (!this.pendingRequestId || reply.requestId !== this.pendingRequestId) return;
    this.ackReceived = true;
    const elapsed = this.scriptSentAt ? Date.now() - this.scriptSentAt : null;
    console.log(
      '[ScriptExecutionManager] Playjector ACK received (script delivery confirmed, latency=%sms)',
      elapsed ?? '?',
    );
  }

  /**
   * Called by the IPC reply routing layer when a launchElevatedPowerShellScript reply arrives.
   */
  handlePlayjectorReply(reply: PlaycastMessageSystemLaunchElevatedPowerShellScriptReply['message']): void {
    if (!this.pendingRequestId || reply.requestId !== this.pendingRequestId) return;
    if (this.pendingReplyCallback) {
      const cb = this.pendingReplyCallback;
      this.pendingReplyCallback = null;
      this.pendingRequestId = null;
      cb(reply);
    } else {
      console.warn('[ScriptExecutionManager] Received Playjector reply with no pending callback');
    }
  }

  /**
   * Execute a single script end-to-end:
   * 1. Send PROVISIONING_SCRIPT_STARTED via WS
   * 2. Send matchmaking pause if needed
   * 3. Forward script to Playjector via IPC
   * 4. Await Playjector reply
   * 5. Send PROVISIONING_SCRIPT_RESULT via WS
   * 6. Send matchmaking resume if needed
   */
  async execute(script: QueuedScript): Promise<ScriptExecutionResult> {
    if (this.ownership?.isMaintenanceOwned(script.executionId)) throw new Error('maintenance_service_owned_execution');
    const machineId = this.getMachineId();
    const hostId = this.getHostId();
    this.cancellationRequested = false;

    this.sendStarted(script, machineId);

    if (script.pauseMatchmaking) {
      this.sendMatchmakingPause(script.deploymentId, machineId);
    }

    let result: ScriptExecutionResult;
    try {
      result = await this.forwardToPlayjector(script, hostId);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : String(err);
      console.error('[ScriptExecutionManager] Playjector execution error:', errorMsg);
      result = { status: 'failed', error: errorMsg };
    }

    this.sendResult(script, machineId, result);

    if (script.pauseMatchmaking) {
      this.sendMatchmakingResume(script.deploymentId, machineId);
    }

    return result;
  }

  private forwardToPlayjector(script: QueuedScript, hostId: string): Promise<ScriptExecutionResult> {
    return new Promise<ScriptExecutionResult>((resolve) => {
      const requestId = createId();
      this.pendingRequestId = requestId;
      const scriptTimeoutMs = (script.timeoutSeconds ?? 300) * 1000;
      const REPLY_TIMEOUT_MS = scriptTimeoutMs + 180_000; // script timeout + 3 min buffer for helper/COM/IPC overhead
      this.ackReceived = false;
      this.scriptSentAt = Date.now();

      const timeoutHandle = setTimeout(() => {
        this.pendingReplyCallback = null;
        this.pendingRequestId = null;
        console.error(
          '[ScriptExecutionManager] Playjector reply timeout for execution=%s (ackReceived=%s)',
          script.executionId,
          this.ackReceived,
        );
        resolve({ status: 'timeout', error: 'playjector_reply_timeout', exitCode: -1 });
      }, REPLY_TIMEOUT_MS);

      this.pendingReplyCallback = (reply) => {
        clearTimeout(timeoutHandle);
        if (reply.error === 'cancelled' || this.cancellationRequested) {
          resolve({
            status: 'cancelled',
            exitCode: reply.exitCode ?? -2,
            stdout: reply.stdout,
            stderr: reply.stderr,
            error: 'cancelled',
          });
        } else if (reply.success) {
          resolve({
            status: 'succeeded',
            exitCode: reply.exitCode ?? 0,
            stdout: reply.stdout,
            stderr: reply.stderr,
          });
        } else if (reply.error === 'timeout') {
          resolve({
            status: 'timeout',
            exitCode: reply.exitCode ?? -1,
            stdout: reply.stdout,
            stderr: reply.stderr,
            error: 'script_timeout',
          });
        } else {
          resolve({
            status: 'failed',
            exitCode: reply.exitCode,
            stdout: reply.stdout,
            stderr: reply.stderr,
            error: reply.error ?? 'execution_failed',
          });
        }
      };

      const ipcMessage = generators.system.launchElevatedPowerShellScript(
        hostId,
        script.completeScript,
        'C:\\',
        undefined,
        {
          requestId,
          fromProvisioning: true,
          scriptSignature: script.signature,
          timeoutSeconds: script.timeoutSeconds,
        },
      );

      console.log(
        '[ScriptExecutionManager] Forwarding to Playjector: deployment=%s execution=%s',
        script.deploymentId,
        script.executionId,
      );
      try {
        this.ipcService.send(ipcMessage);
      } catch (error) {
        clearTimeout(timeoutHandle);
        this.pendingReplyCallback = null;
        this.pendingRequestId = null;
        throw error;
      }
    });
  }

  private sendWsMessage(message: WebsocketMessage): void {
    try {
      this.realtimeService.sendMessage(message);
    } catch (err) {
      console.error('[ScriptExecutionManager] Failed to send WS message:', err);
    }
  }

  private sendStarted(script: QueuedScript, machineId: string): void {
    const payload: Record<string, unknown> = {
      deploymentId: script.deploymentId,
      executionId: script.executionId,
      machineId,
    };
    if (script.stepIndex !== undefined) payload.stepIndex = script.stepIndex;
    this.sendWsMessage(
      generateProvisioningMessage(ProvisioningMessageType.PROVISIONING_SCRIPT_STARTED, payload),
    );
  }

  private sendResult(script: QueuedScript, machineId: string, result: ScriptExecutionResult): void {
    const payload: Record<string, unknown> = {
      deploymentId: script.deploymentId,
      executionId: script.executionId,
      machineId,
      status: result.status,
    };
    if (result.exitCode !== undefined) payload.exitCode = result.exitCode;
    if (result.stdout) payload.stdout = result.stdout;
    if (result.stderr) payload.stderr = result.stderr;
    if (result.status === 'succeeded') {
      payload.error = null;
    } else if (result.error !== undefined) {
      payload.error = result.error;
    }
    if (script.stepIndex !== undefined) payload.stepIndex = script.stepIndex;

    this.sendWsMessage(
      generateProvisioningMessage(ProvisioningMessageType.PROVISIONING_SCRIPT_RESULT, payload),
    );
  }

  private sendMatchmakingPause(deploymentId: string, machineId: string): void {
    this.sendWsMessage(
      generateProvisioningMessage(ProvisioningMessageType.PROVISIONING_MATCHMAKING_PAUSE, {
        deploymentId,
        machineId,
      }),
    );
  }

  private sendMatchmakingResume(deploymentId: string, machineId: string): void {
    this.sendWsMessage(
      generateProvisioningMessage(ProvisioningMessageType.PROVISIONING_MATCHMAKING_RESUME, {
        deploymentId,
        machineId,
      }),
    );
  }

  /**
   * Send a cancel request to Playjector for the currently executing script.
   * The script result will arrive via the normal reply callback (with error='cancelled').
   */
  cancelExecution(options?: { exitGuestMode?: boolean; rebootIfSafe?: boolean }): void {
    this.cancellationRequested = true;
    const hostId = this.getHostId();
    const ipcMessage = generators.system.cancelProvisioningScript(hostId, {
      exitGuestMode: options?.exitGuestMode,
      rebootIfSafe: options?.rebootIfSafe,
    });
    console.log('[ScriptExecutionManager] Sending cancelProvisioningScript IPC');
    this.ipcService.send(ipcMessage);
  }

  /**
   * Called by the coordinator when the cancelProvisioningScript reply arrives from Playjector.
   */
  handleCancelReply(reply: PlaycastMessageSystemCancelProvisioningScriptReply['message']): void {
    console.log(
      '[ScriptExecutionManager] Cancel reply: success=%s wasCancelled=%s error=%s',
      reply.success,
      reply.wasCancelled,
      reply.error ?? 'none',
    );
  }
}
